import type { Candle, Timeframe } from "../types";
import { ema, trueRange } from "../indicators/math";
import { StructureEngine } from "../structure/StructureEngine";
import type { StructureResult } from "../structure/types";

export type Side = "bull" | "bear";

export interface Component {
  id: "bos" | "displacement" | "tickDominance" | "fvg" | "sweep" | "volume" | "htf";
  label: string;
  max: number;
  bull: number;
  bear: number;
  state: string; // human-readable state, e.g. "bullish BOS", "strong", "neutral", "n/a"
  detail?: string;
}

export interface ConfluenceResult {
  time: number;
  bull: number;
  bear: number;
  bias: Side | "neutral";
  components: Component[];
  weights: Record<Component["id"], number>;
}

export const WEIGHTS: Record<Component["id"], number> = { bos: 20, displacement: 20, tickDominance: 15, fvg: 15, sweep: 15, volume: 10, htf: 5 };
const ALL_FLAGS = { swings: true, bos: true, choch: true, fvg: true, ob: false, liq: true };
const RECENT = 20;

function aggregate(candles: Candle[], n: number): Candle[] {
  const out: Candle[] = [];
  for (let i = 0; i < candles.length; i += n) {
    const g = candles.slice(i, i + n);
    out.push({ ...g[0], endTime: g[g.length - 1].endTime, high: Math.max(...g.map((c) => c.high)), low: Math.min(...g.map((c) => c.low)), close: g[g.length - 1].close,
      volume: g.reduce((s, c) => s + c.volume, 0), upTicks: g.reduce((s, c) => s + c.upTicks, 0), downTicks: g.reduce((s, c) => s + c.downTicks, 0) });
  }
  return out;
}

// Deterministic, explainable Felacoo BOS confluence. Separate bullish / bearish 0–100 scores.
// Uses ONLY the candles passed in (i.e. everything ≤ the live tick / replay cursor) — no look-ahead.
export const ConfluenceEngine = {
  score(candles: Candle[], tf: Timeframe, structure?: StructureResult | null): ConfluenceResult | null {
    const N = candles.length;
    if (N < 30) return null;
    const s = structure ?? StructureEngine.analyze(candles, ALL_FLAGS);
    if (!s) return null;
    const last = candles[N - 1];
    const lastIdx = N - 1;
    const atr = ema(trueRange(candles), 14);
    const atrNow = atr[lastIdx] || (last.high - last.low) || 1;
    const comps: Component[] = [];
    const W = WEIGHTS;

    // 1. Structure — most recent BOS / CHoCH within RECENT candles
    const ev = [...s.events].reverse().find((e) => e.index >= lastIdx - RECENT);
    let evDir: Side | null = null;
    if (ev) {
      evDir = ev.dir;
      const pts = ev.type === "BOS" ? W.bos : Math.round(W.bos * 0.75);
      comps.push({ id: "bos", label: "Break of Structure", max: W.bos, bull: evDir === "bull" ? pts : 0, bear: evDir === "bear" ? pts : 0, state: `${evDir === "bull" ? "bullish" : "bearish"} ${ev.type}`, detail: `${lastIdx - ev.index} bars ago @ ${ev.price}` });
    } else comps.push({ id: "bos", label: "Break of Structure", max: W.bos, bull: 0, bear: 0, state: "none" });

    // 2. Displacement — size of the impulse that broke structure vs ATR
    if (ev) {
      const seg = candles.slice(Math.max(0, ev.from), ev.index + 1);
      const move = evDir === "bull" ? Math.max(...seg.map((c) => c.high)) - Math.min(...seg.map((c) => c.low)) : Math.max(...seg.map((c) => c.high)) - Math.min(...seg.map((c) => c.low));
      const ratio = move / (atrNow * Math.max(1, Math.sqrt(seg.length)));
      const state = ratio >= 2.5 ? "strong" : ratio >= 1.2 ? "normal" : "none";
      const pts = state === "strong" ? W.displacement : state === "normal" ? Math.round(W.displacement * 0.6) : 0;
      comps.push({ id: "displacement", label: "Displacement", max: W.displacement, bull: evDir === "bull" ? pts : 0, bear: evDir === "bear" ? pts : 0, state, detail: `${ratio.toFixed(1)}× ATR-normalised` });
    } else comps.push({ id: "displacement", label: "Displacement", max: W.displacement, bull: 0, bear: 0, state: "none" });

    // 3. Tick dominance — share of up-ticks over the recent window (needs tick-built candles)
    const win = candles.slice(-RECENT);
    const ups = win.reduce((a, c) => a + c.upTicks, 0), downs = win.reduce((a, c) => a + c.downTicks, 0);
    if (ups + downs > 0) {
      const share = ups / (ups + downs);
      const strong = share >= 0.6 || share <= 0.4, mild = share >= 0.55 || share <= 0.45;
      const pts = strong ? W.tickDominance : mild ? Math.round(W.tickDominance * 0.5) : 0;
      const dir: Side | null = share > 0.5 ? "bull" : share < 0.5 ? "bear" : null;
      comps.push({ id: "tickDominance", label: "Tick Dominance", max: W.tickDominance, bull: dir === "bull" ? pts : 0, bear: dir === "bear" ? pts : 0, state: pts ? `${dir === "bull" ? "bullish" : "bearish"} ${(share * 100).toFixed(0)}%` : "neutral", detail: `${ups}↑ / ${downs}↓` });
    } else comps.push({ id: "tickDominance", label: "Tick Dominance", max: W.tickDominance, bull: 0, bear: 0, state: "n/a (no tick data)" });

    // 4. FVG — unfilled gap created recently
    const fresh = s.fvg.filter((g) => g.index >= lastIdx - RECENT && g.index < lastIdx);
    const unfilled = fresh.filter((g) => !candles.slice(g.index + 1).some((c) => (g.dir === "bull" ? c.low <= g.bottom : c.high >= g.top)));
    const fb = unfilled.some((g) => g.dir === "bull"), fr = unfilled.some((g) => g.dir === "bear");
    comps.push({ id: "fvg", label: "Fair Value Gap", max: W.fvg, bull: fb ? W.fvg : 0, bear: fr ? W.fvg : 0, state: fb && fr ? "both" : fb ? "bullish FVG" : fr ? "bearish FVG" : "none", detail: `${unfilled.length} open` });

    // 5. Liquidity sweep — wick through a prior swing, close back inside (stop hunt) within RECENT*1.5 bars
    let sweep: Side | null = null;
    const from = Math.max(1, lastIdx - Math.round(RECENT * 1.5));
    for (let i = lastIdx; i >= from && !sweep; i--) {
      const c = candles[i];
      const ph = s.highs.filter((h) => h.index < i - 1).slice(-3);
      const pl = s.lows.filter((l) => l.index < i - 1).slice(-3);
      if (ph.some((h) => c.high > h.price && c.close < h.price)) sweep = "bear";
      else if (pl.some((l) => c.low < l.price && c.close > l.price)) sweep = "bull";
    }
    comps.push({ id: "sweep", label: "Liquidity Sweep", max: W.sweep, bull: sweep === "bull" ? W.sweep : 0, bear: sweep === "bear" ? W.sweep : 0, state: sweep ? `${sweep === "bull" ? "sell-side" : "buy-side"} liquidity taken` : "none" });

    // 6. Volume confirmation — recent tick volume vs 20-bar average, credited to the recent candle direction
    const vols = candles.slice(-RECENT - 1, -1).map((c) => c.volume);
    const avg = vols.reduce((a, b) => a + b, 0) / (vols.length || 1);
    if (avg > 0 && last.volume > 0) {
      const ratio = last.volume / avg;
      const pts = ratio >= 1.5 ? W.volume : ratio >= 1.2 ? Math.round(W.volume * 0.5) : 0;
      const dir: Side = last.close >= last.open ? "bull" : "bear";
      comps.push({ id: "volume", label: "Volume", max: W.volume, bull: dir === "bull" ? pts : 0, bear: dir === "bear" ? pts : 0, state: pts ? `expansion ${ratio.toFixed(1)}×` : "normal" });
    } else comps.push({ id: "volume", label: "Volume", max: W.volume, bull: 0, bear: 0, state: "n/a" });

    // 7. HTF alignment — 5× aggregation of the same ticks, EMA8 vs EMA21
    const htf = aggregate(candles, 5);
    const closes = htf.map((c) => c.close);
    const e8 = ema(closes, 8), e21 = ema(closes, 21);
    const a = e8[e8.length - 1], b = e21[e21.length - 1];
    const htfDir: Side | null = a != null && b != null ? (a > b ? "bull" : a < b ? "bear" : null) : null;
    comps.push({ id: "htf", label: "HTF Alignment", max: W.htf, bull: htfDir === "bull" ? W.htf : 0, bear: htfDir === "bear" ? W.htf : 0, state: htfDir ? `${htfDir === "bull" ? "bullish" : "bearish"} HTF trend` : "n/a" });

    const bull = comps.reduce((t, c) => t + c.bull, 0);
    const bear = comps.reduce((t, c) => t + c.bear, 0);
    return { time: last.time, bull, bear, bias: bull > bear ? "bull" : bear > bull ? "bear" : "neutral", components: comps, weights: W };
  },
};
